import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Plus, Pencil, Trash2, Check, X, PackageOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => new Intl.NumberFormat('id-ID').format(n);

const EMPTY_FORM = { id: '', label: '', coins: '', bonus: '', price: '' };

export const AdminPackages = () => {
  const [packages, setPackages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editingId, setEditingId] = useState(null); // id paket yang sedang diedit
  const [editForm, setEditForm]   = useState({});
  const [showAdd, setShowAdd]     = useState(false);
  const [addForm, setAddForm]     = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState(null); // { type: 'success'|'error', text }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/packages`, { withCredentials: true });
      setPackages(data);
    } catch {
      setMsg({ type: 'error', text: 'Gagal memuat paket.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setEditForm({ label: pkg.label, coins: pkg.coins, bonus: pkg.bonus, price: pkg.price });
    setShowAdd(false);
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async (pkgId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/packages/${pkgId}`, {
        label: editForm.label,
        coins: parseInt(editForm.coins),
        bonus: parseInt(editForm.bonus) || 0,
        price: parseInt(editForm.price),
      }, { withCredentials: true });
      setMsg({ type: 'success', text: 'Paket berhasil diperbarui.' });
      setEditingId(null);
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const deletePackage = async (pkgId) => {
    if (!window.confirm('Hapus paket ini?')) return;
    try {
      await axios.delete(`${API}/admin/packages/${pkgId}`, { withCredentials: true });
      setMsg({ type: 'success', text: 'Paket dihapus.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Gagal menghapus.' });
    } finally {
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const saveAdd = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/packages`, {
        id: addForm.id.trim().toLowerCase().replace(/\s+/g, '_'),
        label: addForm.label,
        coins: parseInt(addForm.coins),
        bonus: parseInt(addForm.bonus) || 0,
        price: parseInt(addForm.price),
      }, { withCredentials: true });
      setMsg({ type: 'success', text: 'Paket baru ditambahkan.' });
      setShowAdd(false);
      setAddForm(EMPTY_FORM);
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Gagal menambah paket.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <PackageOpen size={22} className="inline mr-2 mb-1" />
              Kelola Paket Topup
            </h1>
            <p className="text-slate-500 text-sm mt-1">Atur harga, koin, dan bonus setiap paket Coin Guru</p>
          </div>
          <button
            onClick={() => { setShowAdd(true); setEditingId(null); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
          >
            <Plus size={16} /> Tambah Paket
          </button>
        </div>

        {/* Notifikasi */}
        {msg && (
          <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium ${
            msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {msg.type === 'success' ? '✅' : '⚠'} {msg.text}
          </div>
        )}

        {/* Form Tambah Paket */}
        {showAdd && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">
            <h3 className="font-bold text-blue-900 mb-4 text-sm uppercase tracking-wide">Paket Baru</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">ID Paket</label>
                <input value={addForm.id} onChange={e => setAddForm(f => ({...f, id: e.target.value}))}
                  placeholder="cth: mini" className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Label</label>
                <input value={addForm.label} onChange={e => setAddForm(f => ({...f, label: e.target.value}))}
                  placeholder="cth: Mini" className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Koin</label>
                <input type="number" value={addForm.coins} onChange={e => setAddForm(f => ({...f, coins: e.target.value}))}
                  placeholder="5" className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Bonus Koin</label>
                <input type="number" value={addForm.bonus} onChange={e => setAddForm(f => ({...f, bonus: e.target.value}))}
                  placeholder="0" className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Harga (Rp)</label>
                <input type="number" value={addForm.price} onChange={e => setAddForm(f => ({...f, price: e.target.value}))}
                  placeholder="10000" className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowAdd(false); setAddForm(EMPTY_FORM); }}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">Batal</button>
              <button onClick={saveAdd} disabled={saving}
                className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                {saving ? 'Menyimpan...' : 'Simpan Paket'}
              </button>
            </div>
          </div>
        )}

        {/* Daftar Paket */}
        {loading ? (
          <div className="text-center py-12 text-slate-400 text-sm">Memuat paket...</div>
        ) : (
          <div className="flex flex-col gap-3">
            {packages.map(pkg => {
              const isEditing = editingId === pkg.id;
              const total = pkg.coins + pkg.bonus;
              return (
                <div key={pkg.id} className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all ${
                  isEditing ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-100'
                }`}>
                  {isEditing ? (
                    /* Form Edit */
                    <div className="p-5">
                      <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-4">Edit Paket — <span className="font-mono">{pkg.id}</span></p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {[
                          { key: 'label', label: 'Label', type: 'text', placeholder: 'Pemula' },
                          { key: 'price', label: 'Harga (Rp)', type: 'number', placeholder: '37500' },
                          { key: 'coins', label: 'Koin', type: 'number', placeholder: '15' },
                          { key: 'bonus', label: 'Bonus Koin', type: 'number', placeholder: '0' },
                        ].map(({ key, label, type, placeholder }) => (
                          <div key={key}>
                            <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
                            <input
                              type={type}
                              value={editForm[key]}
                              onChange={e => setEditForm(f => ({...f, [key]: e.target.value}))}
                              placeholder={placeholder}
                              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                            />
                          </div>
                        ))}
                      </div>
                      {/* Preview */}
                      <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4 text-xs text-slate-500">
                        Preview: <strong className="text-slate-700">{editForm.label}</strong> — {parseInt(editForm.coins||0) + parseInt(editForm.bonus||0)} koin total —{' '}
                        <strong className="text-blue-700">Rp {fmt(parseInt(editForm.price||0))}</strong>
                        {' '}(Rp {editForm.price && editForm.coins ? fmt(Math.round(parseInt(editForm.price) / (parseInt(editForm.coins) + parseInt(editForm.bonus||0)))) : 0}/koin)
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={cancelEdit}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50">
                          <X size={14} /> Batal
                        </button>
                        <button onClick={() => saveEdit(pkg.id)} disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}>
                          <Check size={14} /> {saving ? 'Menyimpan...' : 'Simpan'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Row Normal */
                    <div className="flex items-center justify-between px-5 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-lg">🪙</div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 text-sm">{pkg.label}</span>
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-mono">{pkg.id}</span>
                            {pkg.bonus > 0 && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">+{pkg.bonus} bonus</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {pkg.coins} koin{pkg.bonus > 0 ? ` + ${pkg.bonus} bonus` : ''} = <strong>{total} koin total</strong>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-blue-900 text-base">Rp {fmt(pkg.price)}</p>
                          <p className="text-xs text-slate-400">Rp {fmt(Math.round(pkg.price / total))}/koin</p>
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => startEdit(pkg)}
                            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deletePackage(pkg.id)}
                            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-6">
          💡 Perubahan harga langsung berlaku untuk semua user baru yang topup.
        </p>
      </div>
    </DashboardLayout>
  );
};
